import React, {useMemo, useState} from "react";
import axios from "axios";
import {router, useForm} from "@inertiajs/react";
import {Badge, Button, Container, Form, Modal, Table} from "react-bootstrap";
import {ReactSelectBootstrap} from "react-select-bootstrap";
import {allExpanded, defaultStyles, JsonView} from "react-json-view-lite";
import KeyValueTable from "../Components/KeyValueTable.jsx";
import UserFormFields from "../Components/Users/UserFormFields.jsx";

const emptyColumnFilters = {
    name: "",
    email: "",
    weclapp_id: "",
    department_id: "",
    role_id: "",
};

const normalise = value => String(value ?? "").trim().toLocaleLowerCase("de");
const roleLabel = name => (name === "superadmin" ? "Superadmin" : name);

export default function Users({
    users = [],
    roles = [],
    departments = [],
    can_manage: canManage = false,
}) {
    const [selected, setSelected] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [inlineError, setInlineError] = useState("");
    const [query, setQuery] = useState("");
    const [columnFilters, setColumnFilters] = useState(emptyColumnFilters);
    const [statusFilter, setStatusFilter] = useState("active");
    const [sort, setSort] = useState({key: "name", direction: "asc"});
    const [imagePreview, setImagePreview] = useState(null);
    const [showCopySettingsModal, setShowCopySettingsModal] = useState(false);
    const [selectedUserForCopy, setSelectedUserForCopy] = useState(null);
    const editForm = useForm({
        name: "",
        email: "",
        weclapp_id: "",
        role_id: [],
        department_ids: [],
        password: "",
        color: "#000000",
        image: null,
        homepage_route: "",
        is_active: true,
        delete_image: false,
    });

    const sortableValue = (user, key) => {
        if (key === "roles") return (user.roles || []).map(role => role.name).join(", ");
        if (key === "departments") return (user.departments || []).map(department => department.name).join(", ");
        if (key === "is_active") return user.is_active ? "1" : "0";
        return user[key] || "";
    };

    const searchableValue = user => [
        user.name,
        user.email,
        user.weclapp_id,
        ...(user.roles || []).map(role => roleLabel(role.name)),
        ...(user.departments || []).map(department => department.name),
        user.is_active ? "aktiv" : "inaktiv deaktiviert",
    ].join(" ");

    const visibleUsers = useMemo(() => users
        .filter(user => statusFilter === "all" || (statusFilter === "active") === !!user.is_active)
        .filter(user => normalise(user.name).includes(normalise(columnFilters.name)))
        .filter(user => normalise(user.email).includes(normalise(columnFilters.email)))
        .filter(user => normalise(user.weclapp_id).includes(normalise(columnFilters.weclapp_id)))
        .filter(user => !columnFilters.department_id || (user.departments || []).some(department => String(department.id) === columnFilters.department_id))
        .filter(user => !columnFilters.role_id || (user.roles || []).some(role => String(role.id) === columnFilters.role_id))
        .filter(user => normalise(searchableValue(user)).includes(normalise(query)))
        .sort((a, b) => String(sortableValue(a, sort.key)).localeCompare(
            String(sortableValue(b, sort.key)),
            "de",
            {numeric: true, sensitivity: "base"},
        ) * (sort.direction === "asc" ? 1 : -1)), [users, query, columnFilters, statusFilter, sort]);

    const updateColumnFilter = (key, value) => setColumnFilters(current => ({...current, [key]: value}));
    const resetFilters = () => {
        setQuery("");
        setColumnFilters(emptyColumnFilters);
        setStatusFilter("active");
        setSort({key: "name", direction: "asc"});
    };

    const openEdit = user => {
        editForm.clearErrors();
        setImagePreview(null);
        setSelectedUserForCopy(null);
        editForm.setData({
            name: user.name,
            email: user.email,
            weclapp_id: user.weclapp_id || "",
            role_id: (user.roles || []).map(role => role.id),
            department_ids: (user.departments || []).map(department => department.id),
            password: "",
            color: user.color || "#000000",
            image: user.preview_image || user.image || null,
            homepage_route: user.homepage_route || "",
            is_active: !!user.is_active,
            delete_image: false,
        });
        setSelected(user);
    };

    const closeEdit = () => {
        if (editForm.processing) return;
        setSelected(null);
        setImagePreview(null);
        editForm.clearErrors();
    };

    const submitEdit = event => {
        event.preventDefault();
        if (!selected) return;
        editForm.post(`/user/${selected.id}`, {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                setSelected(null);
                setImagePreview(null);
            },
        });
    };

    const sortBy = key => setSort(current => ({
        key,
        direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));

    const sortHeader = (key, label) => (
        <button
            type="button"
            className="btn btn-link p-0 text-decoration-none fw-semibold"
            onClick={() => sortBy(key)}
        >
            {label}
            {sort.key === key && <span className="ms-1" aria-label={sort.direction === "asc" ? "aufsteigend" : "absteigend"}>{sort.direction === "asc" ? "▲" : "▼"}</span>}
        </button>
    );

    const saveInline = async (user, field, ids) => {
        if (!canManage || !user.can_manage) return;
        setSavingId(`${user.id}:${field}`);
        setInlineError("");
        try {
            await axios.patch(
                `/users/${user.id}/${field === "roles" ? "roles-inline" : "departments-inline"}`,
                field === "roles" ? {role_id: ids} : {department_ids: ids},
            );
            router.reload({
                only: ["users"],
                preserveScroll: true,
                onFinish: () => setSavingId(null),
            });
        } catch (error) {
            setInlineError(error.response?.data?.message || "Änderung konnte nicht gespeichert werden.");
            setSavingId(null);
        }
    };

    const copySettings = () => {
        if (!selected || !selectedUserForCopy) return;
        router.get(`/user/copysettings/${selectedUserForCopy.value}/${selected.id}`);
        setShowCopySettingsModal(false);
    };

    const copyUserOptions = users
        .filter(user => user.id !== selected?.id)
        .map(user => ({value: user.id, label: user.name}));
    const jsonStyle = {...defaultStyles, container: "json-view-container"};
    const hasChangedFilters = query !== ""
        || statusFilter !== "active"
        || Object.values(columnFilters).some(Boolean)
        || sort.key !== "name"
        || sort.direction !== "asc";
    const menuPortalTarget = typeof document === "undefined" ? undefined : document.body;

    return (
        <Container fluid className="py-3">
            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Benutzer</h1>
                    <small className="text-muted">Rollen, Abteilungen und Weclapp-Zuordnung auf einen Blick</small>
                </div>
                {canManage && <Button onClick={() => router.get("/user/create")}>Benutzer anlegen</Button>}
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                <Form.Control
                    aria-label="Alle Benutzerspalten durchsuchen"
                    placeholder="Alle Spalten durchsuchen…"
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    style={{maxWidth: 360}}
                />
                <div className="d-flex gap-2 align-items-center small text-muted">
                    <span>{visibleUsers.length} von {users.length} Benutzern</span>
                    {hasChangedFilters && <button type="button" className="btn btn-link btn-sm p-0" onClick={resetFilters}>Filter und Sortierung zurücksetzen</button>}
                </div>
            </div>

            {inlineError && <div className="alert alert-danger" role="alert">{inlineError}</div>}

            <Table hover responsive className="align-middle bg-white">
                <thead>
                    <tr>
                        <th aria-sort={sort.key === "name" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("name", "Name")}</th>
                        <th aria-sort={sort.key === "email" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("email", "E-Mail")}</th>
                        <th aria-sort={sort.key === "weclapp_id" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("weclapp_id", "Weclapp-ID")}</th>
                        <th aria-sort={sort.key === "departments" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("departments", "Abteilungen")}</th>
                        <th aria-sort={sort.key === "roles" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("roles", "Rollen")}</th>
                        <th aria-sort={sort.key === "is_active" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>{sortHeader("is_active", "Status")}</th>
                        <th aria-label="Aktionen"/>
                    </tr>
                    <tr className="table-light align-top">
                        <th><Form.Control size="sm" aria-label="Name filtern" value={columnFilters.name} onChange={event => updateColumnFilter("name", event.target.value)} placeholder="Filtern…"/></th>
                        <th><Form.Control size="sm" aria-label="E-Mail filtern" value={columnFilters.email} onChange={event => updateColumnFilter("email", event.target.value)} placeholder="Filtern…"/></th>
                        <th><Form.Control size="sm" aria-label="Weclapp-ID filtern" value={columnFilters.weclapp_id} onChange={event => updateColumnFilter("weclapp_id", event.target.value)} placeholder="Filtern…"/></th>
                        <th>
                            <Form.Select size="sm" aria-label="Abteilung filtern" value={columnFilters.department_id} onChange={event => updateColumnFilter("department_id", event.target.value)}>
                                <option value="">Alle</option>
                                {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                            </Form.Select>
                        </th>
                        <th>
                            <Form.Select size="sm" aria-label="Rolle filtern" value={columnFilters.role_id} onChange={event => updateColumnFilter("role_id", event.target.value)}>
                                <option value="">Alle</option>
                                {roles.map(role => <option key={role.id} value={role.id}>{roleLabel(role.name)}</option>)}
                            </Form.Select>
                        </th>
                        <th>
                            <Form.Select size="sm" aria-label="Status filtern" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                                <option value="active">Nur aktive</option>
                                <option value="inactive">Nur inaktive</option>
                                <option value="all">Alle</option>
                            </Form.Select>
                        </th>
                        <th/>
                    </tr>
                </thead>
                <tbody>
                    {visibleUsers.map(user => (
                        <tr key={user.id}>
                            <td className="fw-semibold">
                                {user.name}
                                {user.is_protected && <Badge bg="dark" pill className="ms-2">geschützt</Badge>}
                            </td>
                            <td>{user.email}</td>
                            <td>{user.weclapp_id || "—"}</td>
                            <td aria-busy={savingId === `${user.id}:departments`} style={{minWidth: 220}}>
                                {canManage && user.can_manage ? (
                                    <ReactSelectBootstrap
                                        aria-label={`Abteilungen von ${user.name}`}
                                        isMulti
                                        menuPortalTarget={menuPortalTarget}
                                        menuPosition="fixed"
                                        isDisabled={savingId !== null}
                                        options={departments.map(department => ({value: department.id, label: department.name}))}
                                        value={(user.departments || []).map(department => ({value: department.id, label: department.name}))}
                                        onChange={value => saveInline(user, "departments", (value || []).map(option => option.value))}
                                    />
                                ) : (user.departments || []).length ? (user.departments || []).map(department => (
                                    <Badge bg="light" text="dark" className="me-1 mb-1" key={department.id}>{department.name}</Badge>
                                )) : "—"}
                            </td>
                            <td aria-busy={savingId === `${user.id}:roles`} style={{minWidth: 220}}>
                                {canManage && user.can_manage ? (
                                    <ReactSelectBootstrap
                                        aria-label={`Rollen von ${user.name}`}
                                        isMulti
                                        menuPortalTarget={menuPortalTarget}
                                        menuPosition="fixed"
                                        isDisabled={savingId !== null}
                                        options={roles.map(role => ({value: role.id, label: roleLabel(role.name)}))}
                                        value={(user.roles || []).map(role => ({value: role.id, label: roleLabel(role.name)}))}
                                        onChange={value => saveInline(user, "roles", (value || []).map(option => option.value))}
                                    />
                                ) : (user.roles || []).map(role => (
                                    <Badge pill bg={role.name === "superadmin" ? "dark" : "primary"} className="me-1 mb-1" key={role.id}>{roleLabel(role.name)}</Badge>
                                ))}
                            </td>
                            <td><Badge bg={user.is_active ? "success" : "secondary"}>{user.is_active ? "Aktiv" : "Deaktiviert"}</Badge></td>
                            <td className="text-end">
                                {canManage && user.can_manage && <Button size="sm" variant="outline-primary" onClick={() => openEdit(user)}>Bearbeiten</Button>}
                            </td>
                        </tr>
                    ))}
                    {visibleUsers.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">Keine Benutzer für diese Filter gefunden.</td></tr>}
                </tbody>
            </Table>

            <Modal show={Boolean(selected)} onHide={closeEdit} size="xl" scrollable>
                <Form
                    onSubmit={submitEdit}
                    className="d-flex flex-column overflow-hidden"
                    style={{maxHeight: "calc(100dvh - 3.5rem)"}}
                >
                    <Modal.Header closeButton className="flex-shrink-0">
                        <Modal.Title>Benutzer bearbeiten: {selected?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="overflow-auto" style={{minHeight: 0}}>
                        <UserFormFields
                            data={editForm.data}
                            setData={editForm.setData}
                            errors={editForm.errors}
                            roles={roles}
                            departments={departments}
                            editable={!editForm.processing}
                            imagePreview={imagePreview}
                            onImagePreview={setImagePreview}
                        />
                        <hr/>
                        <KeyValueTable
                            title="Benutzereinstellungen"
                            button={<Button type="button" variant="outline-primary" onClick={() => setShowCopySettingsModal(true)}>Einstellungen kopieren</Button>}
                            keyHeader="Schlüssel"
                            valueHeader="Wert"
                            data={selected?.settings_data || {}}
                            valueRenderer={value => <JsonView style={jsonStyle} data={value} shouldExpandNode={allExpanded}/>}
                        />
                    </Modal.Body>
                    <Modal.Footer className="flex-shrink-0">
                        <Button type="button" variant="light" disabled={editForm.processing} onClick={closeEdit}>Abbrechen</Button>
                        <Button type="submit" disabled={editForm.processing}>{editForm.processing ? "Wird gespeichert…" : "Speichern"}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showCopySettingsModal} onHide={() => setShowCopySettingsModal(false)}>
                <Modal.Header closeButton><Modal.Title>Einstellungen eines Benutzers übernehmen</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ReactSelectBootstrap
                        options={copyUserOptions}
                        value={selectedUserForCopy}
                        onChange={setSelectedUserForCopy}
                        placeholder="Benutzer auswählen…"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowCopySettingsModal(false)}>Abbrechen</Button>
                    <Button disabled={!selectedUserForCopy} onClick={copySettings}>Einstellungen kopieren</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
